import * as React from 'react';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ContextualMenu, DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import {
    addLieferungPosition,
    LieferungPosition,
    defaultLieferungPosition,
    updateLieferungPosition,
    useLieferungPositionList,
} from 'api/lieferungPosition';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { cloneDeep, isEqual } from 'lodash';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { EinheitByCode } from 'formComponents/lookups/EinheitByCode';
import { FormTextarea, FormTextareaOptions } from 'formComponents/FormTextarea';
import { Timestamps } from 'formComponents/Timestamps';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { Kunde } from 'api/kunde';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { Lieferung } from 'api/lieferung';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { logDifference } from 'utils/helper';
import { LieferungPositionChangeMenge } from './LieferungPositionChangeMenge';
import { ArtikelDetailSelect } from '../Lager/ArtikelDetail/ArtikelDetailSelect';

const styles = mergeStyleSets({
    artikelLine: {
        marginTop: 5,
        display: 'grid',
        // gridTemplateColumns: '80px 1fr 3fr',
        gridTemplateColumns: '80px 1fr',
        gridColumnGap: 10,
    },
    textLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
        gridColumnGap: 10,
    },
    addLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '6fr 1fr',
        gridColumnGap: 10,
    },
    posLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gridColumnGap: 10,
    },
    lastLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '3fr 1fr',
        gridColumnGap: 10,
    },
    varianteHeader: {
        marginTop: 15,
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 700,
        backgroundColor: '#EEEEEE',
        paddingTop: 10,
    },
    variante: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: 10,
        backgroundColor: '#EEEEEE',
        padding: '0 10px 10px 10px',
    },
    changeMengeGrid: {
        marginTop: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '120px 120px',
        gridRowGap: 10,
        gridColumnGap: 10,
    },
    newMengeLabel: {
        marginTop: 6,
    },
    newMenge: {
        textAlign: 'right',
    },
});

interface LieferungPositionEditProps {
    kunde: Kunde;
    lieferung: Lieferung;
    lNr: string;
    pos: number; // -1 = new
    readOnly?: boolean;
    onClose: (refetch: boolean) => void;
}

export const LieferungPositionEdit: React.FC<LieferungPositionEditProps> = ({
    kunde,
    lieferung,
    lNr,
    pos,
    readOnly = false,
    onClose,
}) => {
    const auth = useSelector(authSelector);

    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();
    const [showChangeMenge, setShowChangeMenge] = React.useState(false);

    const lieferungPositionList = useLieferungPositionList(
        {
            lNr,
            pos,
        },
        { enabled: pos !== -1 },
    );

    const [artikelDetailKalkulationen, setArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);

    const [lieferungPosition, setLieferungPosition] = React.useState<LieferungPosition | null>(null);

    React.useEffect(() => {
        if (!lieferungPositionList.isLoading && !lieferungPositionList.isFetching && lieferungPosition === null) {
            if (lieferungPositionList.data?.length === 1) {
                setLieferungPosition(cloneDeep(lieferungPositionList.data[0]));
                setSavingState(SavingState.notChanged);

                if (lieferungPositionList.data[0].artikelGruppeNr && lieferungPositionList.data[0].artikelNr) {
                    getArtikelDetailKalkulationen({
                        artikelGruppeNr: lieferungPositionList.data[0].artikelGruppeNr,
                        artikelNr: lieferungPositionList.data[0].artikelNr,
                    }).then((newArtikelDetailKalkulationen) =>
                        setArtikelDetailKalkulationen(newArtikelDetailKalkulationen),
                    );
                }
            } else {
                setLieferungPosition({
                    ...defaultLieferungPosition,
                    kNr: kunde.nr,
                    lNr,
                    pos,
                    posMwst: kunde.land === 'AT' ? 20 : 0,
                });
                setSavingState(SavingState.notSaved);
            }
        }
    }, [lieferungPosition, lieferungPositionList, lNr, pos, auth, kunde]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !lieferungPositionList.isLoading &&
            !lieferungPositionList.isFetching &&
            lieferungPositionList.data?.length === 1 &&
            lieferungPosition !== null &&
            !isEqual(lieferungPositionList.data[0], lieferungPosition)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('lieferungPosition', lieferungPosition, lieferungPositionList.data[0]);
        }
    }, [savingState, lieferungPositionList, lieferungPosition]);

    React.useEffect(() => {
        if (lieferungPosition) {
            if (lieferungPosition.posMenge && lieferungPosition.posEPreis) {
                const netto = Math.round(lieferungPosition.posMenge * lieferungPosition.posEPreis * 100) / 100;
                const mwstFactor = lieferungPosition.posMwst ? 1 + lieferungPosition?.posMwst / 100 : 1;
                const brutto = lieferungPosition.posMwst ? Math.round(netto * mwstFactor * 100) / 100 : netto;
                setLieferungPosition({
                    ...lieferungPosition,
                    posNetto: netto,
                    posBrutto: brutto,
                });
            } else {
                setLieferungPosition({
                    ...lieferungPosition,
                    posNetto: 0,
                    posBrutto: 0,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lieferungPosition?.posMenge, lieferungPosition?.posEPreis, lieferungPosition?.posMwst]);

    const validate = (): boolean => {
        if (!lieferungPosition?.posText?.trim()) {
            setValidationError('Bitte geben Sie einen Text ein.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }
        return true;
    };

    const save = () => {
        if (lieferungPosition && savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                if (lieferungPosition.pos === -1) {
                    addLieferungPosition(lieferungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                } else {
                    updateLieferungPosition(lieferungPosition)
                        .then(() => {
                            setSavingState(SavingState.saved);
                            if (isMounted) {
                                setSavingState(SavingState.notChanged);
                            }
                            onClose(true);
                        })
                        .catch((error) => {
                            console.error(error);
                            setSavingState(SavingState.error);
                        });
                }
            }
        }
    };

    const renderTextField = (column: keyof LieferungPosition, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(lieferungPosition?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof LieferungPosition,
        label: string,
        options: FormDecimalFieldOptions = {},
    ) => (
        <FormDecimalField
            value={(lieferungPosition?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextarea = (column: keyof LieferungPosition, label: string, options: FormTextareaOptions = {}) => (
        <FormTextarea
            value={lieferungPosition?.[column] as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderArtikelDetailSelect = () => (
        <ArtikelDetailSelect
            artikelGruppeNr={lieferungPosition?.artikelGruppeNr}
            artikelNr={lieferungPosition?.artikelNr}
            disabled={lieferungPosition?.pos !== -1}
            onChange={async (newArtikelDetail) => {
                if (lieferungPosition) {
                    if (!newArtikelDetail || !newArtikelDetail.artikel) {
                        setLieferungPosition({
                            ...lieferungPosition,
                            artikelGruppeNr: defaultLieferungPosition.artikelGruppeNr,
                            artikelNr: defaultLieferungPosition.artikelNr,
                            posText: defaultLieferungPosition.posText,
                            posEkPreis: defaultLieferungPosition.posEkPreis,
                            posEPreis: defaultLieferungPosition.posEPreis,
                            posEinh: defaultLieferungPosition.posEinh,
                            lArt: defaultLieferungPosition.lArt,
                        });
                        return;
                    }

                    setArtikelDetailKalkulationen(null);

                    const newArtikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                    });

                    setArtikelDetailKalkulationen(newArtikelDetailKalkulationen);

                    const defaultKalkulation = newArtikelDetailKalkulationen.find(
                        (k) => k.eh === newArtikelDetail.artikel?.einheit,
                    );

                    const ekPreis = defaultKalkulation?.ek2 || 0;
                    // const ePreis =
                    // Math.round(ekPreis * ((100 + (defaultKalkulation?.vkProz || 0)) / 100) * 1000) / 1000;
                    const ePreis = defaultKalkulation?.vk || 0;

                    setLieferungPosition({
                        ...lieferungPosition,
                        artikelGruppeNr: newArtikelDetail.artikelGruppeNr,
                        artikelNr: newArtikelDetail.artikelNr,
                        posText: newArtikelDetail.artikel?.text || '',
                        posEkPreis: ekPreis,
                        posEPreis: ePreis,
                        posEinh: newArtikelDetail.artikel?.einheit || '',
                        lArt: newArtikelDetail.lagerIntNr === 0 ? 'D' : 'L',
                    });
                }
            }}
        />
    );

    const renderEinheit = (
        column: keyof LieferungPosition,
        ekPreisColumn: keyof LieferungPosition,
        ePreisColumn: keyof LieferungPosition,
        label: string,
    ) => (
        <EinheitByCode
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            value={(lieferungPosition?.[column] as string) || ''}
            menuPlacement="top"
            onChange={(newEinheit) => {
                let ekPreis = 0;
                let ePreis = 0;
                if (artikelDetailKalkulationen && newEinheit) {
                    const kalkulation = artikelDetailKalkulationen.find((k) => k.eh === newEinheit);
                    ekPreis = kalkulation?.ek2 || 0;
                    ePreis = kalkulation?.vk || 0;
                }

                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        [column]: newEinheit,
                        [ekPreisColumn]: ekPreis,
                        [ePreisColumn]: ePreis,
                    });
                }
            }}
        />
    );

    const renderLieferArt = () => (
        <LieferArt
            allowEmpty
            disabled={savingState === SavingState.saving || readOnly || lieferungPosition?.pos !== -1}
            value={(lieferungPosition?.lArt as string) || ''}
            menuPlacement="top"
            onChange={(newValue) => {
                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        lArt: newValue || '',
                    });
                }
            }}
        />
    );

    /*
    const renderCheckbox = (column: keyof LieferungPosition, label: string) => (
        <Checkbox
            className={formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving || readOnly}
            checked={lieferungPosition?.[column] as boolean}
            onChange={(event, checked) => {
                if (lieferungPosition) {
                    setLieferungPosition({
                        ...lieferungPosition,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );
     */

    return (
        <>
            <Dialog
                hidden={false}
                title="Lieferungsposition"
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth={1100}
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    {/*
                    <div className={styles.artikelLine}>
                        <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                        <div className={formStyles.gridCell}>{renderArtikelGruppe()}</div>
                        <div className={formStyles.gridCell}>{renderArtikel()}</div>
                    </div>
                    */}
                    <div className={styles.artikelLine}>
                        <div>{renderTextField('pos', 'Position', { numeric: true, disabled: true })}</div>
                        <div>{renderArtikelDetailSelect()}</div>
                    </div>
                    <div className={formStyles.line}>
                        <div>{renderTextField('posText', 'Text', { maxLength: 100 })}</div>
                    </div>
                    <div className={formStyles.line}>{renderTextarea('posBem', 'Bemerkung zur Position')}</div>
                    <div className={styles.addLine}>
                        <div />
                        <div>{renderLieferArt()}</div>
                    </div>
                    <div className={styles.posLine}>
                        <div>{renderEinheit('posEinh', 'posEkPreis', 'posEPreis', 'Einheit')}</div>
                        <div>{renderDecimalField('posMenge', 'Menge', { disabled: pos !== -1, fixed: 3 })}</div>
                        <div>{renderDecimalField('posAMenge', 'A-Menge', { fixed: 3, disabled: true })}</div>
                        <div>{renderDecimalField('posEkPreis', 'EK-Preis', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posEPreis', 'VK-Preis', { fixed: 3 })}</div>
                        <div>{renderDecimalField('posNetto', 'VK Netto', { disabled: true })}</div>
                        <div>{renderDecimalField('posMwst', 'MwSt.', { fixed: 0 })}</div>
                        <div>{renderDecimalField('posBrutto', 'VK Brutto', { disabled: true })}</div>
                    </div>

                    <Timestamps
                        modifiedDate={lieferungPosition?.updDat}
                        modifiedDecimalTime={lieferungPosition?.updUhr}
                        modifiedUser={lieferungPosition?.updUser}
                        showCreated={false}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.migrateButton}
                            onClick={() => setShowChangeMenge(true)}
                            text="Lieferart / Menge ändern"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                lieferungPositionList.isLoading ||
                                !lieferungPosition?.pos
                            }
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || lieferungPositionList.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(lieferungPositionList.isLoading || lieferungPositionList.isFetching || !lieferungPosition) && (
                    <LoadingIndicator />
                )}
            </Dialog>
            {showChangeMenge && lieferungPosition && (
                <LieferungPositionChangeMenge
                    lieferung={lieferung}
                    lieferungPosition={lieferungPosition}
                    onClose={(refetch) => {
                        setShowChangeMenge(false);
                        if (refetch) {
                            onClose(true);
                        }
                    }}
                />
            )}
        </>
    );
};
